import {CUSTOM_FIELD_RESOURCES} from '@/components/query/custom-fields';
import _ from '@/common/helpers';
import FILTER_TYPES from '@/components/query/filter-types';
import FILTER_SOURCE_TYPES from '@/components/query/filter-source-type';
import SpecResolver from '@/common/spec/spec-resolver';

const {
    paymentCardSchemes: {enum: gatewayPaymentCardSchemes},
} = SpecResolver.resolveEntity('GatewayAccount');

/* eslint-disable quote-props */
export default {
    id: 'transactions',
    version: 3,
    filterSpec: {
        id: {
            type: FILTER_TYPES.text,
        },
        'customer.tags': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.rebillyAPI,
                path: 'tags',
                labelBuilder: ({name}) => name,
                value: 'name',
            },
        },
        'websiteId': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.rebillyAPI,
                path: 'websites',
                labelBuilder: ({id, name}) => `${_.truncate(id, {limit: 11, dots: true})} - ${name}`,
                value: 'id',
            },
        },
        'gatewayAccountId': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.rebillyAPI,
                path: 'gatewayAccounts',
                labelBuilder: ({id}) => id,
                value: 'id',
            },
        },
        'planIds': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.rebillyAPI,
                path: 'plans',
                labelBuilder: ({id, name}) => `${_.truncate(id, {limit: 11, dots: true})} - ${name}`,
                value: 'id',
            },
        },
        'childTransactions': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.rebillyAPI,
                path: 'transactions',
                labelBuilder: ({id}) => _.truncate(id, {limit: 11, dots: true}),
                value: 'id',
            },
        },
        'paymentCardId': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.rebillyAPI,
                path: 'paymentCards',
                labelBuilder: ({id}) => _.truncate(id, {limit: 11, dots: true}),
                value: 'id',
            },
        },
        'subscriptionIds': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.rebillyAPI,
                path: 'subscriptions',
                labelBuilder: ({id}) => _.truncate(id, {limit: 11, dots: true}),
                value: 'id',
            },
        },
        'organizationId': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.rebillyAPI,
                path: 'companies',
                labelBuilder: ({id}) => id,
                value: 'id',
            },
        },
        'currency': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.currencies,
            },
        },
        'method': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.definitions,
                path: 'Method/enum',
            },
        },
        'gatewayName': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.definitions,
                path: 'GatewayAccount/gatewayName/enum',
            },
        },
        'acquirerName': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.definitions,
                path: 'GatewayAccount/acquirerName/enum',
            },
        },
        'retriesResult': {
            type: FILTER_TYPES.enum,
            options: {
                source: FILTER_SOURCE_TYPES.definitions,
                path: 'Transaction/retriesResult/enum',
            },
        },
        'orderId': {
            type: FILTER_TYPES.text,
        },
        'requestId': {
            type: FILTER_TYPES.text,
        },
        'customerId': {
            type: FILTER_TYPES.text,
        },
        'hasDcc': {
            type: FILTER_TYPES.boolean,
        },
        'bin': {
            type: FILTER_TYPES.text,
            format: 'bin',
        },
        'amount': {
            type: FILTER_TYPES.numeric,
        },
        'rebillNumber': {
            type: FILTER_TYPES.numeric,
        },
        'retryNumber': {
            type: FILTER_TYPES.numeric,
        },
        'isRetry': {
            type: FILTER_TYPES.boolean,
        },
        'isDisputed': {
            type: FILTER_TYPES.boolean,
        },
        'isReconciled': {
            type: FILTER_TYPES.boolean,
        },
        'hadDiscrepancy': {
            type: FILTER_TYPES.boolean,
        },
        'isRebill': {
            type: FILTER_TYPES.boolean,
        },
        'riskScore': {
            type: FILTER_TYPES.numeric,
        },
        'has3ds': {
            type: FILTER_TYPES.boolean,
        },
        'velocity': {
            type: FILTER_TYPES.numeric,
        },
        'type': {
            type: FILTER_TYPES.enum,
            options: {
                source: FILTER_SOURCE_TYPES.definitions,
                path: 'Transaction/type/enum',
            },
        },
        'result': {
            type: FILTER_TYPES.enum,
            options: {
                source: FILTER_SOURCE_TYPES.definitions,
                path: 'Transaction/result/enum',
            },
        },
        'status': {
            type: FILTER_TYPES.enum,
            options: {
                source: FILTER_SOURCE_TYPES.definitions,
                path: 'Transaction/status/enum',
            },
        },
        'scheduledTime': {
            type: FILTER_TYPES.datetime,
            options: {
                timePicker: true,
            },
        },
        'processedTime': {
            type: FILTER_TYPES.datetime,
            options: {
                timePicker: true,
            },
        },
        'createdTime': {
            type: FILTER_TYPES.datetime,
            options: {
                timePicker: true,
            },
        },
        'updatedTime': {
            type: FILTER_TYPES.datetime,
            options: {
                timePicker: true,
            },
        },
        // billing address
        'billingAddress/firstName': {
            type: FILTER_TYPES.text,
        },
        'billingAddress/lastName': {
            type: FILTER_TYPES.text,
        },
        'billingAddress/address': {
            type: FILTER_TYPES.text,
        },
        'billingAddress/address2': {
            type: FILTER_TYPES.text,
        },
        'billingAddress/organization': {
            type: FILTER_TYPES.text,
        },
        'billingAddress/city': {
            type: FILTER_TYPES.text,
        },
        'billingAddress/region': {
            type: FILTER_TYPES.text,
        },
        'billingAddress/country': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.countries,
            },
        },
        'billingAddress/postalCode': {
            type: FILTER_TYPES.text,
        },
        'billingAddress/email': {
            type: FILTER_TYPES.text,
        },
        'billingAddress/phoneNumber': {
            type: FILTER_TYPES.text,
        },

        // gateway response
        gatewayResponseCode: {
            type: FILTER_TYPES.text,
        },
        gatewayResponseOriginalCode: {
            type: FILTER_TYPES.text,
        },
        gatewayResponseOriginalMessage: {
            type: FILTER_TYPES.text,
        },
        avsGatewayResponseOriginalCode: {
            type: FILTER_TYPES.text,
        },
        avsGatewayResponseOriginalMessage: {
            type: FILTER_TYPES.text,
        },
        cvsGatewayResponseOriginalCode: {
            type: FILTER_TYPES.text,
        },
        cvsGatewayResponseOriginalMessage: {
            type: FILTER_TYPES.text,
        },

        // lead source
        'leadSource/campaign': {
            type: FILTER_TYPES.text,
        },
        'leadSource/medium': {
            type: FILTER_TYPES.text,
        },
        'leadSource/source': {
            type: FILTER_TYPES.text,
        },
        'leadSource/term': {
            type: FILTER_TYPES.text,
        },
        'leadSource/content': {
            type: FILTER_TYPES.text,
        },
        'leadSource/affiliate': {
            type: FILTER_TYPES.text,
        },
        'leadSource/subAffiliate': {
            type: FILTER_TYPES.text,
        },
        'leadSource/salesAgent': {
            type: FILTER_TYPES.text,
        },
        'leadSource/amount': {
            type: FILTER_TYPES.numeric,
        },
        'leadSource/path': {
            type: FILTER_TYPES.text,
        },
        // risk meta data
        'riskMetadata/ipAddress': {
            type: FILTER_TYPES.text,
        },
        'riskMetadata/isProxy': {
            type: FILTER_TYPES.boolean,
        },
        'riskMetadata/isVpn': {
            type: FILTER_TYPES.boolean,
        },
        'riskMetadata/isTor': {
            type: FILTER_TYPES.boolean,
        },
        'riskMetadata/isHosting': {
            type: FILTER_TYPES.boolean,
        },
        'riskMetadata/vpnServiceName': {
            type: FILTER_TYPES.text,
        },
        'riskMetadata/isp': {
            type: FILTER_TYPES.text,
        },
        'riskMetadata/country': {
            type: FILTER_TYPES.text,
        },
        'riskMetadata/city': {
            type: FILTER_TYPES.text,
        },
        'riskMetadata/postalCode': {
            type: FILTER_TYPES.text,
        },
        'riskMetadata/latitude': {
            type: FILTER_TYPES.numeric,
        },
        'riskMetadata/longitude': {
            type: FILTER_TYPES.numeric,
        },
        'riskMetadata/accuracyRadius': {
            type: FILTER_TYPES.text,
        },
        'riskMetadata/timeZone': {
            type: FILTER_TYPES.text,
        },
        'riskMetadata/fingerprint': {
            type: FILTER_TYPES.text,
        },
        'riskMetadata/distance': {
            type: FILTER_TYPES.numeric,
        },
        'last4': {
            label: 'Last 4',
            type: FILTER_TYPES.text,
        },
        // custom fields
        'customer/customFields': {
            type: FILTER_TYPES.custom,
            options: {resourceType: CUSTOM_FIELD_RESOURCES.customers},
        },
        'paymentCard/customFields': {
            type: FILTER_TYPES.custom,
            options: {resourceType: CUSTOM_FIELD_RESOURCES.paymentCards},
        },
        'paymentCard/brand': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.enum,
                enum: gatewayPaymentCardSchemes.sort(),
            },
        },
        'paymentCard/bankName': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.rebillyAPI,
                path: 'paymentCardsBankNames',
                labelBuilder: ({name}) => name,
                value: 'name',
            },
        },
        'paymentCard/bankCountry': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.countries,
            },
        },
        'hasBumpOffer': {
            type: FILTER_TYPES.boolean,
        },
        'bumpOffer.outcome': {
            type: FILTER_TYPES.select,
            options: {
                source: FILTER_SOURCE_TYPES.enum,
                enum: [
                    'unknown',
                    'selected',
                    'rejected',
                ],
            },
        },
        'revision': {
            type: FILTER_TYPES.numeric,
        },
        'isProcessedOutside': {
            type: FILTER_TYPES.boolean,
        },
    },
};
